<template>
    <div
        :id="group + '_' + name + '_label'"
        class="tab-bar-label"
        :class="[{ 'is-active': active, 'is-valid': valid, 'is-invalid': invalid, 'step-label': !!useStep, disabled, 'show-tooltip': disabled }, 'tab-bar-label-' + group]"
        v-interact="() => changeTab(true)"
        type="button"
        role="tab"
        :aria-selected="active ? 'true' : 'false'"
        :aria-controls="group + '_' + name"
        ref="el"
    >
    <span v-if="useStep" class="step-label-indicator">
      <i v-if="validated" class="material-icons md-18" aria-hidden="true" v-text="isValid ? 'check' : 'close'" role="img"></i>
      <span class="step-number">{{useStep}}.</span>
    </span>
        <slot :is-valid="isValid" :validated="validated" :title="title">
            <span class="tab-bar-label-text">{{title}}</span>
        </slot>
    </div>
</template>

<script lang="ts" setup>
    import {usePortuna} from '@/composables/portuna';
    import {NoOperation, applyChangesPrompt} from '@/library/utils';
    import {isDeveloper} from '@/utils/portuna';
    import {computed, inject, onMounted, onUnmounted, ref, watch} from 'vue';
    import {FormStepsKey} from '@/library/form';
    import {type RouteParamsGeneric, useRoute, useRouter} from 'vue-router';
    import {useStore} from 'vuex';
    import LocalStorage from '../library/localStorage';
    import TopMessageHandler from '@/library/topMessageHandler';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'activated', name: string): void;
        (eventName: 'save', value: any): void
    }>();

    const props = withDefaults(
        defineProps<{
            name: string;
            group: string;
            default?: boolean;
            title?: string;
            useStep?: number | false;
            forceShow?: boolean;
            preventSubmitOnBack?: boolean;
            loading?: boolean;
            disabled?: boolean;
            stepIndex?: number;
        }>(),
        {
            default: false,
            title: '',
            useStep: false,
            forceShow: false,
            preventSubmitOnBack: false,
            loading: false,
            disabled: false,
        },
    );

    const ctx = inject<Nullable<IFormStepsInternalContext>>(FormStepsKey, null);

    const route  = useRoute();
    const router = useRouter();
    const store  = useStore();

    const el = ref<HTMLElement | null>(null);

    const validated     = ref(false);
    const isValid       = ref(0);
    const instanceIndex = ref(0);

    const options = computed<TabBarData>(() => store.state.tabBar[props.group]);

    const active = computed(() => props.forceShow || (options.value && options.value.active === props.name));

    const localStorageKey = computed(() => `tab-bar-active.${props.group}`);

    const valid = computed(() => validated.value && !!isValid.value);

    const invalid = computed(() => validated.value && !isValid.value);

    const useStorage = computed(() => options.value?.store);

    function update(data: Partial<TabBarData>)
    {
        store.commit(
            'updateTabBar',
            {
                group: props.group,
                data: Object.assign({}, options.value, data),
            },
        );
    }

    watch(() => route.params, (params: RouteParamsGeneric) =>
    {
        if (!options.value)
        {
            return;
        }

        if (options.value.paramName && params?.[options.value.paramName] && params?.[options.value.paramName] !== options.value.active)
        {
            update({active: params[options.value.paramName] as string});
        }
    }, {deep: true});

    watch(() => props.group, init);
    init();

    function init()
    {
        if (!options.value)
        {
            return;
        }

        if (!options.value.first)
        {
            update({first: props.name});
        }

        const param = route.params?.[options.value.paramName as any];
        if (options.value.paramName && param)
        {
            update({active: param as string});

            emit('activated', param as string);

            portuna.emitter.emit('activated', param);
        }

        const saved = LocalStorage.get(localStorageKey.value, null);
        if (useStorage.value && !options.value.active && saved)
        {
            update({active: saved});

            emit('activated', saved);

            portuna.emitter.emit('activated', saved);
        }

        if (!options.value.active && props.default)
        {
            update({active: props.name});

            emit('activated', props.name);

            portuna.emitter.emit('activated', props.name);
        }
    }

    function activateTab()
    {
        update({active: props.name});

        emit('activated', props.name);

        portuna.emitter.emit('activated', props.name);

        if (options.value?.paramName)
        {
            const routeData: any = {
                name: route.name,
                hash: route.hash,
                query: route.query,
                params: {...route.params},
                force: true,
            };

            if (routeData.params)
            {
                if (routeData.params[options.value.paramName] === props.name)
                {
                    return;
                }

                routeData.params[options.value.paramName] = props.name;
            }

            router.replace(routeData).catch(NoOperation);
        }

        if (useStorage.value)
        {
            LocalStorage.set(localStorageKey.value, props.name);
        }
    }

    function changeTab(labelClicked = false)
    {
        if (props.loading || props.disabled)
        {
            return;
        }

        if (!ctx)
        {
            activateTab();

            return;
        }

        let index = 0;
        for (let i = 0; i < ctx.labelCount.value; i++)
        {
            if (ctx.getLabel(i)?.name === props.name)
            {
                index = i;

                break;
            }
        }

        if (ctx.maxReached.value + 1 < index || (labelClicked && index === ctx.activeIndex.value))
        {
            return;
        }

        ctx.intention.value = index < ctx.activeIndex.value ? 'previous' : 'next';
        if (ctx.intention.value === 'previous')
        {
            if (!ctx.stepAsComponent.value || props.preventSubmitOnBack)
            {
                ctx.requestChange(index, {ignoreValidation: true});

                return;
            }

            applyChangesPrompt()
            .then(() => validateAndSetStep(index))
            .catch(() => ctx.requestChange(index, {ignoreValidation: true}));

            return;
        }
        else if (labelClicked && ctx.maxReached.value < index)
        {
            if (ctx.activeIndex.value === ctx.maxReached.value && index === ctx.maxReached.value + 1)
            {
                ctx.next();

                return;
            }

            for (let index = 0; index <= ctx.maxReached.value; index++)
            {
                if (!ctx.getLabel(index)?.isValid)
                {
                    return;
                }
            }
        }

        validateAndSetStep(index);
    }

    async function validateAndSetStep(index: number)
    {
        try
        {
            if (!ctx)
            {
                activateTab();

                return;
            }

            await ctx.validateCurrent();

            if (!ctx.stepAsComponent.value)
            {
                await ctx.requestChange(index, {ignoreValidation: true});

                return;
            }

            const currentContent = ctx.getContent(ctx.activeIndex.value);
            if (!currentContent)
            {
                throw new Error('no matching tab-bar-content found');
            }

            const formComponent = await currentContent.getFirstFormComponent();
            if (!formComponent || typeof formComponent.save !== 'function')
            {
                throw new Error('form component does not own a save method');
            }

            const onSaveResolved = (data: any) =>
            {
                if (typeof formComponent.triggerSaveEmit !== 'function')
                {
                    if (isDeveloper())
                    {
                        console.error('Form component is missing the trigger save emit function. Trying fallback now.');
                    }

                    formComponent?.$emit && formComponent.$emit('save', ctx.intention.value);
                    formComponent?.emitter?.emit && formComponent.emitter.emit('save', ctx.intention.value);
                }
                else
                {
                    formComponent.triggerSaveEmit(ctx.intention.value);
                }

                ctx.requestChange(index, {ignoreValidation: true});

                return data;
            };

            if ((portuna.currentUser.value && portuna.currentUser.value.isSupport))
            {
                TopMessageHandler.info('Es wurden keine Daten gespeichert, da Sie als Support-Nutzer angemeldet sind.');

                onSaveResolved(null);

                return;
            }

            await formComponent.save(ctx, ctx.intention.value);

            onSaveResolved(null);
        }
        catch (e)
        {
            console.error('form invalid!', e);

            throw e;
        }
    }

    const exposed = Object.assign({}, portuna, {
        componentName: 'TabBarLabel',
        name: props.name,
        el,
        activateTab,
        changeTab,
        validateAndSetStep,
        validated,
        isValid,
        preventSubmitOnBack: props.preventSubmitOnBack,
    }) as IFormStepLabelContext;

    onMounted(() =>
    {
        if (options.value && !options.value.active)
        {
            activateTab();
        }

        if (!ctx)
        {
            return;
        }

        if (typeof props.stepIndex !== 'undefined' && !isNaN(props.stepIndex))
        {
            instanceIndex.value = Number(props.stepIndex);
        }
        else
        {
            while (ctx.getLabel(instanceIndex.value))
            {
                instanceIndex.value++;
            }
        }

        ctx.registerLabel(instanceIndex.value, exposed);
    });

    onUnmounted(() =>
    {
        if (!ctx)
        {
            return;
        }

        ctx.unregisterLabel(instanceIndex.value);
    });

    defineExpose(exposed);
</script>